import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { payroll } from '../../api';
import { currency } from '../billing/shared';
import { EmployeeDialog } from './EmployeeDialog';

export const EmployeesTab = ({ clientId, toast }) => {
  const [list, setList] = useState(null);
  const [dialog, setDialog] = useState({ open: false, employee: null });
  const load = () => payroll.employees(clientId).then(setList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);
  const remove = (e) => {
    if (!window.confirm(`Remove ${e.firstName} ${e.lastName}?`)) return;
    payroll.deleteEmployee(e.id).then((r) => { toast({ title: r.archived ? 'Employee archived (has finalised payslips)' : 'Employee removed' }); load(); });
  };

  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="payroll-employees-tab">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold text-stone-900"><Users size={16} className="text-emerald-800"/> Employees <span className="text-xs font-normal text-stone-500">{list ? `${list.filter((e) => e.active).length} active` : ''}</span></div>
        <Button size="sm" onClick={() => setDialog({ open: true, employee: null })} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="add-employee-btn"><Plus size={14} className="mr-1.5"/> Add employee</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>{['No.', 'Name', 'Cell phone', 'Tax no.', 'Frequency', 'Pay', 'Flags', ''].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {list && list.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-stone-500" data-testid="employees-empty">No employees yet. Add the client&apos;s staff to start running payroll.</td></tr>}
            {list?.map((e) => (
              <tr key={e.id} className={`hover:bg-stone-50 ${e.active ? '' : 'opacity-50'}`} data-testid={`employee-row-${e.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-stone-500">{e.employeeNumber}</td>
                <td className="px-5 py-3"><div className="font-medium text-stone-900">{e.firstName} {e.lastName}</div><div className="text-xs text-stone-500">{e.jobTitle || e.idNumber || ''}</div></td>
                <td className="px-5 py-3 text-stone-600 whitespace-nowrap" data-testid={`employee-cell-${e.id}`}>{e.cellPhone ? <a href={`tel:${e.cellPhone.replace(/\s/g, '')}`} className="hover:text-emerald-800">{e.cellPhone}</a> : '—'}</td>
                <td className="px-5 py-3 text-stone-600">{e.taxNumber || '—'}</td>
                <td className="px-5 py-3 capitalize text-stone-600">{e.payFrequency}</td>
                <td className="px-5 py-3 text-stone-900 tabular-nums">{e.payType === 'hourly' ? `${currency(e.hourlyRate)}/h` : currency(e.basicSalary)}</td>
                <td className="px-5 py-3 text-xs space-x-1">
                  {e.etiEligible && <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-800">ETI</span>}
                  {e.uifExempt && <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">UIF exempt</span>}
                  {e.directiveRate != null && e.directiveRate !== '' && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">Directive {e.directiveRate}%</span>}
                  {!e.active && <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">Inactive</span>}
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button type="button" onClick={() => setDialog({ open: true, employee: e })} className="p-1 text-stone-400 hover:text-emerald-800" data-testid={`employee-edit-${e.id}`}><Pencil size={15}/></button>
                  <button type="button" onClick={() => remove(e)} className="p-1 text-stone-400 hover:text-red-600" data-testid={`employee-delete-${e.id}`}><Trash2 size={15}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EmployeeDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} employee={dialog.employee} clientId={clientId} onSaved={load} toast={toast}/>
    </div>
  );
};
